import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TransactionCustomerCategory, TransactionCarrierCategory } from '@hero/api';
import { useTransactionListApi } from '@hero/api-hooks';
import { cn } from '@hero/web-lib';
import { TransactionCategoryDynamicIcon } from '@hero/web-icons';
import {
  getTransactionCustomerCategoryClass,
  getTransactionCustomerCategoryIconClass,
  getTransactionCarrierCategoryClass,
  getTransactionCarrierCategoryIconClass,
} from '@hero/web-theme';
import { TransactionList } from '../List/TransactionList';

type Category = TransactionCustomerCategory | TransactionCarrierCategory;

interface CategoryOption {
  value: Category;
  label: string;
  badgeClass: string;
  iconColor: string;
}

export interface TransactionTableManagerProps {
  displayForUser: 'customer' | 'carrier';
}

export function TransactionTableManager({
  displayForUser,
}: TransactionTableManagerProps) {
  const { t, i18n } = useTranslation('m-payment');
  const isRTL = i18n.language === 'ar';

  const [activeCategory, setActiveCategory] = useState<Category | undefined>(undefined);

  const options: CategoryOption[] = useMemo(() => {
    if (displayForUser === 'customer') {
      return (['payments', 'orders', 'cashbacks'] as const).map(cat => ({
        value: cat as Category,
        label: t(`transaction.customer_category.${cat}`),
        badgeClass: getTransactionCustomerCategoryClass(cat),
        iconColor: getTransactionCustomerCategoryIconClass(cat),
      }));
    }
    return (['earnings', 'payouts', 'cashbacks'] as const).map(cat => ({
      value: cat as Category,
      label: t(`transaction.carrier_category.${cat}`),
      badgeClass: getTransactionCarrierCategoryClass(cat),
      iconColor: getTransactionCarrierCategoryIconClass(cat),
    }));
  }, [displayForUser, t]);

  const query = useTransactionListApi(activeCategory ? { category: activeCategory } : {});

  return (
    <div className="flex flex-col gap-4">
      {/* Category filter badges */}
      <div className={cn('flex flex-wrap gap-3 justify-center max-w-2xl mx-auto w-full', isRTL && 'flex-row-reverse')}>
        {options.map(({ value, label, badgeClass, iconColor }) => {
          const isActive = activeCategory === value;

          return (
            <button
              key={value}
              onClick={() => setActiveCategory(isActive ? undefined : value)}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors border',
                isActive
                  ? `${badgeClass} border-transparent`
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300',
              )}>
              <TransactionCategoryDynamicIcon
                category={value}
                displayForUser={displayForUser}
                className={cn('!h-5 !w-5', !isActive && iconColor)}
              />
              {label}
            </button>
          );
        })}
      </div>

      {/* Transaction list */}
      <TransactionList
        query={query}
        displayForUser={displayForUser}
        className="gap-3"
      />
    </div>
  );
}
